<script lang="ts">
  interface Props {
    onImport: () => void
    onFileDrop?: (file: File) => void
  }

  const { onImport, onFileDrop }: Props = $props()

  let isDragging = $state(false)

  function onDragOver(ev: DragEvent) {
    ev.preventDefault()
    isDragging = true
  }
  function onDragLeave() {
    isDragging = false
  }
  function onDrop(ev: DragEvent) {
    ev.preventDefault()
    isDragging = false
    const file = ev.dataTransfer?.files?.[0]
    if (file && onFileDrop) onFileDrop(file)
  }
</script>

<section
  class="flex min-h-[70vh] flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 text-center transition-colors"
  class:border-border-rule={!isDragging}
  class:border-accent={isDragging}
  class:bg-surface-card={!isDragging}
  class:bg-accent-deep={isDragging}
  ondragover={onDragOver}
  ondragleave={onDragLeave}
  ondrop={onDrop}
  aria-label="Welcome — drop a city export to import"
>
  <h2 class="text-3xl font-semibold text-text-primary">Welcome to Nexus Forge.</h2>
  <p class="mt-3 max-w-xl text-text-secondary">
    Plan changes to your Forge of Empires city before you make them in-game. Move buildings around,
    try out new layouts, and stash anything you're not sure about — all without spending a single
    diamond.
  </p>

  <ol
    class="mt-8 grid max-w-3xl grid-cols-1 gap-4 text-left text-sm text-text-primary md:grid-cols-3"
  >
    <li class="rounded-lg bg-surface-card-recessed p-4">
      <span class="block text-xs font-mono uppercase text-text-muted">step 1</span>
      <p class="mt-1">
        Open <span class="font-semibold">Town Overview</span> in
        <a
          href="https://foe-helper.com/"
          target="_blank"
          rel="noopener noreferrer"
          class="text-accent-bright underline-offset-2 hover:underline">FoE Helper</a
        >.
      </p>
    </li>
    <li class="rounded-lg bg-surface-card-recessed p-4">
      <span class="block text-xs font-mono uppercase text-text-muted">step 2</span>
      <p class="mt-1">
        Click <span class="font-semibold">Copy citymap data</span>.
      </p>
    </li>
    <li class="rounded-lg bg-surface-card-recessed p-4">
      <span class="block text-xs font-mono uppercase text-text-muted">step 3</span>
      <p class="mt-1">Paste it here, or drop the file onto this panel.</p>
    </li>
  </ol>

  <button
    type="button"
    class="mt-8 rounded-lg bg-accent px-6 py-3 text-base font-semibold text-white shadow-lg shadow-accent-deep/40 transition hover:bg-accent-bright focus:outline-none focus:ring-2 focus:ring-accent-bright"
    onclick={onImport}>Load your first city</button
  >

  <p class="mt-3 text-xs text-text-muted">
    Inside the editor, press <kbd
      class="rounded border border-border-rule bg-surface-card-recessed px-1.5 py-0.5 font-mono text-[10px] text-text-secondary"
      >?</kbd
    > to see every keyboard shortcut.
  </p>

  {#if isDragging}
    <p class="mt-4 text-sm text-accent-bright">Drop the file to import…</p>
  {/if}

  <footer class="mt-10 max-w-xl text-[11px] leading-relaxed text-text-muted">
    Unofficial fan tool. Forge of Empires is a registered trademark of InnoGames GmbH. This project
    is not affiliated with, endorsed by, or sponsored by InnoGames.
  </footer>
</section>
